/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import DeviceStatus from '../status/DeviceStatus';

export interface DeviceStatusLegendsProps {
  normalText: string;
  thresholdText: string;
  normalColor?: string;
  thresholdColor?: string;
  includeLegends?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const {label} = getAssetDetailsContentStyles();
  return {
    legendContainer : {
      color          : dlsColor('grey3'),
      textTransform  : 'uppercase',
      letterSpacing  : '1px',
      display        : 'flex',
      flexDirection  : 'column',
      alignItems     : 'flex-start',
      justifyContent : 'space-between',
      minHeight      : '32px'
    },
    statusText : {
      fontSize       : '20px',
      width          : '100%',
      display        : 'flex',
      flexDirection  : 'column',
      alignContent   : 'center',
      textAlign      : 'center',
      justifyContent : 'flex-end',
      color          : dlsColor('grey3')
    },
    status : {
      [theme.breakpoints.down('sm')] : {
        padding : '5px 0'
      }
    },
    // maps the legend text to the shared uppercase label style's exact
    // Roboto/10px/14px metrics ...
    currentLegendContainer : {
      fontFamily : label.fontFamily,
      fontWeight : label.fontWeight,
      fontSize   : label.fontSize,
      lineHeight : label.lineHeight
    }
  };
});

/**
 * DeviceStatusLegends is a component used to display the legends for the SC Device Sensor Graphs.
 *
 * ```typescript
 *
 * import {DeviceStatusLegends} from '../src/routes/tracking/sc/reports/DeviceStatusLegends';
 *
 * <DeviceStatusLegends normalText="Normal" thresholdText="Threshold" />
 *
 * ```
 *
 */
const DeviceStatusLegends = ({
  normalText,
  thresholdText,
  includeLegends = true,
  normalColor = dlsColor('caBlue'),
  thresholdColor = dlsColor('caRed')
}: DeviceStatusLegendsProps) => {
  const classes   = useStyles();
  const isCurrent = isCurrentTheme();

  return (
    <div className={themedClassName(classes.legendContainer, classes.currentLegendContainer, isCurrent)}>
      {includeLegends && (
        <>
          <span className={classes.status}>
            <DeviceStatus color={normalColor} text={normalText} />
          </span>
          <span className={classes.status}>
            <DeviceStatus color={thresholdColor} text={thresholdText} />
          </span>
        </>
      )}
    </div>
  );
};

export default DeviceStatusLegends;
